/**
 * Power-Ups screen.
 *
 * Data source: GET /admin/metrics/powerups (Phase C).
 * Shows erasers + 50/50 side-by-side, grant sources, bundle mix and the
 * outstanding-inventory liability snapshots.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  downloadCsv,
  errorState,
  escape,
  fmtBps,
  fmtInt,
  fmtKobo,
  kpi,
  mountInteractiveBarChart,
  mountSortableTable,
  rangeQuery,
  skeletonGrid,
  snapshotKpi,
} from './common.js';

export async function renderPowerups(host, ctx) {
  host.innerHTML = skeletonGrid(6);
  try {
    const data = await api(`/admin/metrics/powerups${rangeQuery(ctx.range)}`);
    host.innerHTML = _render(data);
    _mountDailyCharts(host, data.timeseries || []);
    _mountDailyTable(host, data.timeseries || []);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

export function exportCsv({ range }) {
  const qs = rangeQuery(range);
  const path = qs ? `/admin/metrics/powerups${qs}&format=csv` : '/admin/metrics/powerups?format=csv';
  downloadCsv(path, `triviana_powerups_${range.start}_${range.end}.csv`);
}

function _render(d) {
  const t = d.totals || {};
  // Combined power-up revenue across both SKUs. Prominent hero KPI so
  // the "how much did power-ups make us in this window?" question has
  // a single answer at the top of the page.
  const total = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));">
      ${kpi({
        label: 'Total power-up revenue',
        value: fmtKobo(t.powerup_revenue_kobo),
        hint: 'erasers + 50/50 · windowed',
        hero: true,
      })}
      ${kpi({
        label: 'Eraser revenue',
        value: fmtKobo(t.eraser_revenue_kobo),
        hint: 'this period',
        hero: true,
      })}
      ${kpi({
        label: '50/50 revenue',
        value: fmtKobo(t.fiftyfifty_revenue_kobo),
        hint: 'this period',
        hero: true,
      })}
    </div>
  `;
  const erasers = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(186px,1fr));">
      ${kpi({ label: 'Erasers sold', value: fmtInt(t.eraser_units_sold), hint: 'units' })}
      ${kpi({ label: 'Eraser revenue', value: fmtKobo(t.eraser_revenue_kobo) })}
      ${kpi({ label: 'Erasers used', value: fmtInt(t.erasers_used_total) })}
      ${kpi({ label: 'Arm rate', value: fmtBps(t.eraser_arm_rate_bps), hint: 'window' })}
      ${kpi({ label: 'Use rate', value: fmtBps(t.eraser_use_rate_bps), hint: 'window' })}
      ${kpi({ label: 'Eraser holders', value: fmtBps(t.powerup_holders_eraser_bps), hint: '% of active' })}
    </div>
  `;
  const fifty = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(186px,1fr));">
      ${kpi({ label: '50/50 sold', value: fmtInt(t.fiftyfifty_units_sold), hint: 'units' })}
      ${kpi({ label: '50/50 revenue', value: fmtKobo(t.fiftyfifty_revenue_kobo) })}
      ${kpi({ label: '50/50 used', value: fmtInt(t.fiftyfifty_used_total) })}
      ${kpi({ label: 'Avg / live game', value: (Number(t.avg_fiftyfifty_per_live_game_x100 || 0) / 100).toFixed(2), hint: 'per live event' })}
      ${kpi({ label: '50/50 holders', value: fmtBps(t.powerup_holders_fiftyfifty_bps), hint: '% of active' })}
    </div>
  `;
  const grants = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(186px,1fr));margin-top:14px;">
      ${kpi({ label: '50/50 granted · referral', value: fmtInt(t.fiftyfifty_granted_referral) })}
      ${kpi({ label: '50/50 granted · awarded', value: fmtInt(t.fiftyfifty_granted_awarded) })}
      ${kpi({ label: '50/50 granted · admin', value: fmtInt(t.fiftyfifty_granted_admin) })}
    </div>
  `;
  const snap = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(232px,1fr));margin-top:12px;">
      ${snapshotKpi({ label: 'Outstanding eraser liability', value: fmtInt(t.outstanding_eraser_balance), hint: 'unspent erasers owed' })}
      ${snapshotKpi({ label: 'Outstanding 50/50 liability', value: fmtInt(t.outstanding_fiftyfifty_balance), hint: 'unspent 50/50 owed' })}
    </div>
  `;
  const eraserMix = _bundleMix(d.eraser_bundle_mix || {}, 'Eraser bundle mix');
  const fifMix = _bundleMix(d.fiftyfifty_bundle_mix || {}, '50/50 bundle mix');
  const notAvail = t.powerup_winrate_delta_bps == null
    ? `<div class="adm2-panel" style="margin-top:14px;">
         <div class="adm2-panel-title">Power-up win-rate delta</div>
         <div class="adm2-panel-sub">not tracked yet — winner definition needs normalising (${escape(t.powerup_winrate_delta_reason || '')})</div>
       </div>`
    : '';
  return `
    <div class="adm2-section-label">Power-up revenue · windowed</div>
    ${total}
    <div class="adm2-section-label">Erasers · windowed</div>
    ${erasers}
    <div class="adm2-section-label">50/50 · windowed</div>
    ${fifty}
    <div class="adm2-section-label">Grants (50/50)</div>
    ${grants}
    <div style="display:flex;align-items:center;gap:10px;margin:26px 0 12px;">
      <span style="font:600 12px 'IBM Plex Sans',sans-serif;letter-spacing:.04em;text-transform:uppercase;color:var(--a-warm-ink-2);">Live snapshots</span>
      <span style="flex:1;height:1px;background:var(--a-warm-bd);"></span>
    </div>
    ${snap}

    <div class="adm2-section-label">Daily activity</div>
    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Eraser units sold</div>
        <div class="adm2-panel-sub">hover a bar for the day's revenue + units used</div>
        <div id="pu-eraser-chart" style="margin-top:14px;"></div>
      </div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">50/50 units sold</div>
        <div class="adm2-panel-sub">hover a bar for the day's revenue + units used</div>
        <div id="pu-fifty-chart" style="margin-top:14px;"></div>
      </div>
    </div>

    <div class="adm2-panel" style="margin-top:16px;padding:0;overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Daily breakdown</div>
        <div class="adm2-panel-sub">click a header to sort · most recent first</div>
      </div>
      <div id="pu-daily-table" style="padding:8px 4px;"></div>
    </div>

    <div class="adm2-two">
      ${eraserMix}
      ${fifMix}
    </div>
    ${notAvail}
  `;
}

// ---- Daily charts + table --------------------------------------------------

function _formatDayLabel(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

function _mountDailyCharts(host, ts) {
  const eraserHost = host.querySelector('#pu-eraser-chart');
  const fiftyHost = host.querySelector('#pu-fifty-chart');
  if (eraserHost) {
    mountInteractiveBarChart(eraserHost, {
      data: ts.map((row) => ({
        x: row.date,
        y: Number(row.eraser_units_sold || 0),
        meta: row,
      })),
      color: 'var(--a-warm-ink)',
      yLabel: 'units',
      xFmt: _formatDayLabel,
      tooltipExtra: (row) => `
        <div><span class="k">Revenue</span><span class="v">${escape(fmtKobo(row.meta?.eraser_revenue_kobo))}</span></div>
        <div><span class="k">Used</span><span class="v">${escape(fmtInt(row.meta?.erasers_used_total))}</span></div>
      `,
      emptyMessage: 'No eraser sales in this window.',
    });
  }
  if (fiftyHost) {
    mountInteractiveBarChart(fiftyHost, {
      data: ts.map((row) => ({
        x: row.date,
        y: Number(row.fiftyfifty_units_sold || 0),
        meta: row,
      })),
      color: '#7C53E8',
      yLabel: 'units',
      xFmt: _formatDayLabel,
      tooltipExtra: (row) => `
        <div><span class="k">Revenue</span><span class="v">${escape(fmtKobo(row.meta?.fiftyfifty_revenue_kobo))}</span></div>
        <div><span class="k">Used</span><span class="v">${escape(fmtInt(row.meta?.fiftyfifty_used_total))}</span></div>
      `,
      emptyMessage: 'No 50/50 sales in this window.',
    });
  }
}

function _mountDailyTable(host, ts) {
  const target = host.querySelector('#pu-daily-table');
  if (!target) return;
  mountSortableTable(target, {
    columns: [
      {
        key: 'date',
        label: 'Date',
        text: true,
        get: (r) => r.date || '',
        cell: (r) => escape(_formatDayLabel(r.date)),
      },
      {
        key: 'eraser_units_sold',
        label: 'Erasers sold',
        right: true,
        get: (r) => Number(r.eraser_units_sold || 0),
        cell: (r) => escape(fmtInt(r.eraser_units_sold)),
        className: 'mono',
      },
      {
        key: 'eraser_revenue_kobo',
        label: 'Eraser revenue',
        right: true,
        get: (r) => Number(r.eraser_revenue_kobo || 0),
        cell: (r) => escape(fmtKobo(r.eraser_revenue_kobo)),
        className: 'mono',
      },
      {
        key: 'fiftyfifty_units_sold',
        label: '50/50 sold',
        right: true,
        get: (r) => Number(r.fiftyfifty_units_sold || 0),
        cell: (r) => escape(fmtInt(r.fiftyfifty_units_sold)),
        className: 'mono',
      },
      {
        key: 'fiftyfifty_revenue_kobo',
        label: '50/50 revenue',
        right: true,
        get: (r) => Number(r.fiftyfifty_revenue_kobo || 0),
        cell: (r) => escape(fmtKobo(r.fiftyfifty_revenue_kobo)),
        className: 'mono',
      },
      {
        key: 'erasers_used_total',
        label: 'Erasers used',
        right: true,
        get: (r) => Number(r.erasers_used_total || 0),
        cell: (r) => escape(fmtInt(r.erasers_used_total)),
        className: 'mono',
      },
      {
        key: 'fiftyfifty_used_total',
        label: '50/50 used',
        right: true,
        get: (r) => Number(r.fiftyfifty_used_total || 0),
        cell: (r) => escape(fmtInt(r.fiftyfifty_used_total)),
        className: 'mono',
      },
    ],
    rows: ts,
    defaultSort: { key: 'date', dir: 'desc' },
    minWidth: 820,
    emptyMessage: 'No daily data for this range.',
    pageSizeKey: 'powerups.daily',
  });
}

function _bundleMix(mix, title) {
  const entries = Object.entries(mix || {});
  const total = entries.reduce((acc, [, v]) => acc + Number(v || 0), 0);
  if (!entries.length) {
    return `
      <div class="adm2-panel">
        <div class="adm2-panel-title">${escape(title)}</div>
        <div class="adm2-kpi-hint" style="margin-top:12px;">No purchases in this window.</div>
      </div>
    `;
  }
  const rows = entries
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map(([sku, n]) => {
      const pct = Math.round((100 * Number(n || 0)) / Math.max(1, total));
      return `
        <div style="margin-bottom:12px;">
          <div style="display:flex;justify-content:space-between;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);margin-bottom:6px;">
            <span>${escape(sku)}</span>
            <span style="color:var(--a-muted);font-family:'IBM Plex Mono',monospace;">${escape(fmtInt(n))} · ${pct}%</span>
          </div>
          <div class="adm2-bar-track"><div class="adm2-bar-fill" style="width:${pct}%;background:#7C53E8;"></div></div>
        </div>
      `;
    })
    .join('');
  return `
    <div class="adm2-panel">
      <div class="adm2-panel-title">${escape(title)}</div>
      <div class="adm2-panel-sub">bundle SKUs · unit counts</div>
      <div style="margin-top:14px;">${rows}</div>
    </div>
  `;
}
